import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api.js';
import { fmtHM } from '../lib/format.js';
import {
  TEAM_FILTERS,
  applyTeamFilter,
  partitionExempt,
  teamCounts,
} from '../lib/teamRows.js';
import { ConfirmDialog } from './ConfirmDialog.jsx';
import { ExcludedBadge } from './ExcludedBadge.jsx';
import { Icon } from './Icon.jsx';
import { Select } from './Select.jsx';
import { StatusPill } from './StatusPill.jsx';
import { TeamHealth } from './TeamHealth.jsx';
import { useToast } from './Toast.jsx';

/**
 * v2.0 (A4 / A5 / A7) — approver Team view.
 *
 * Lives behind the Subject Switcher's "Team view" tab. A segmented control
 * switches between two reads of the same month:
 *   • Monthly health (default) — the aggregate roll-up (overall / by
 *     department / by country), rendered by <TeamHealth>.
 *   • By person — one row per report with monthly aggregates and an
 *     approval affordance. Filter pills (All / On track / Warning + Alert /
 *     Pending), bulk "Approve N selected" via row checkboxes, drill-in by
 *     clicking a row.
 * Both modes share the month picker and the same computed rows, so the two
 * views can never disagree on a number.
 *
 * v2.6 — excluded users (loggingExempt, "not required to log time") are
 * partitioned out of every count, filter, and the roll-up; they're listed
 * only in a separate "Excluded · not required to log" group at the bottom
 * of the All view, each marked with an Excluded badge and no metrics.
 */
const MONTH_NAMES = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];
const FLAG_EMOJI = { PH: '🇵🇭', PL: '🇵🇱' };

function monthKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

// Shared between the header row and every body row so the columns stay
// in sync. The name + email column gets the most space because it holds
// two lines of text (name flag + email).
const TEAM_GRID_COLUMNS = '32px 2.4fr 0.9fr 0.9fr 0.9fr 0.9fr 1.3fr';
// Mirror the Summary view's hard floor — ClockIn went live in May 2026.
const MONTH_HARD_FLOOR = '2026-05';
function buildMonthOptions(today) {
  const opts = [];
  for (let i = 0; i < 12; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const key = monthKey(d);
    if (key < MONTH_HARD_FLOOR) break;
    opts.push({
      key,
      label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}`,
    });
  }
  return opts;
}

export function TeamView({ role, onDrillIn }) {
  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
  const monthOptions = useMemo(() => buildMonthOptions(today), [today]);
  const [month, setMonth] = useState(monthOptions[0].key);
  // 'overview' (Monthly health roll-up) | 'detail' (per-person table).
  // Monthly health is the default landing read.
  const [mode, setMode] = useState('overview');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // all | risky | pending
  const [selected, setSelected] = useState(() => new Set());
  const [busyEmail, setBusyEmail] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const toast = useToast();

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.teamMonth(month);
      setRows(data.rows || []);
      setSelected(new Set());
    } catch (err) {
      setError(err.message || 'Could not load team view');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [month]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // v2.6 — excluded users (loggingExempt) are split out before any count,
  // filter, or bulk-approve set is computed, so they never move a stat.
  // They're still rendered, but only ever from `exempt` (the Excluded
  // group at the bottom of the All view).
  const { tracked, exempt } = useMemo(() => partitionExempt(rows), [rows]);
  const filtered = useMemo(
    () => applyTeamFilter(tracked, filter),
    [tracked, filter],
  );
  const counts = useMemo(() => teamCounts(tracked), [tracked]);
  // Whether the Excluded group is shown — only under the "All" filter, and
  // only when at least one excluded user is in scope.
  const showExcludedGroup = filter === 'all' && exempt.length > 0;

  const selectableEmails = filtered
    .filter((r) => r.approval.state !== 'approved')
    .map((r) => r.email);
  const checkedEmails = selectableEmails.filter((e) => selected.has(e));
  const allChecked =
    selectableEmails.length > 0 &&
    checkedEmails.length === selectableEmails.length;
  const someChecked = checkedEmails.length > 0 && !allChecked;

  const toggleOne = (email) =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });

  const toggleAll = () =>
    setSelected((s) => {
      const next = new Set(s);
      if (allChecked) {
        selectableEmails.forEach((e) => next.delete(e));
      } else {
        selectableEmails.forEach((e) => next.add(e));
      }
      return next;
    });

  const approveSingle = (row) =>
    setConfirm({
      title: `Lock ${
        monthOptions.find((o) => o.key === month)?.label
      } for ${row.name}?`,
      body: `${row.name} won't be able to add, edit, or delete entries for this month until you unlock it.`,
      confirmLabel: 'Approve & lock',
      onConfirm: async () => {
        setBusyEmail(row.email);
        try {
          await api.approveMonth(row.email, month);
          toast(
            `Locked ${row.name}'s ${
              monthOptions.find((o) => o.key === month)?.label
            }`,
          );
          await refresh();
        } catch (err) {
          toast(err.message || 'Could not approve', 'error');
        } finally {
          setBusyEmail(null);
          setConfirm(null);
        }
      },
    });

  const approveSelected = () => {
    if (checkedEmails.length === 0) return;
    const targets = checkedEmails.slice();
    setConfirm({
      title: `Approve & lock for ${targets.length} report${
        targets.length === 1 ? '' : 's'
      }?`,
      body:
        targets
          .map((em) => rows.find((r) => r.email === em)?.name)
          .filter(Boolean)
          .join(', ') + '.',
      confirmLabel: `Approve ${targets.length}`,
      onConfirm: async () => {
        setBusyEmail('__all');
        let okCount = 0;
        for (const email of targets) {
          try {
            await api.approveMonth(email, month);
            okCount += 1;
          } catch {
            /* keep going; per-row failures logged below */
          }
        }
        toast(
          okCount === targets.length
            ? `Locked ${okCount} report${okCount === 1 ? '' : 's'}`
            : `Locked ${okCount} of ${targets.length} — some failed`,
          okCount === targets.length ? 'success' : 'error',
        );
        setBusyEmail(null);
        setConfirm(null);
        setSelected(new Set());
        await refresh();
      },
    });
  };

  const reopen = (row) =>
    setConfirm({
      title: `Reopen ${
        monthOptions.find((o) => o.key === month)?.label
      } for ${row.name}?`,
      body: `${row.name} will regain the ability to edit entries for this month. The original approval is recorded.`,
      confirmLabel: 'Reopen month',
      danger: true,
      onConfirm: async () => {
        setBusyEmail(row.email);
        try {
          await api.reopenMonth(row.email, month);
          toast(
            `Reopened ${row.name}'s ${
              monthOptions.find((o) => o.key === month)?.label
            }`,
            'error',
          );
          await refresh();
        } catch (err) {
          toast(err.message || 'Could not reopen', 'error');
        } finally {
          setBusyEmail(null);
          setConfirm(null);
        }
      },
    });

  const monthLabel = monthOptions.find((o) => o.key === month)?.label || '';

  return (
    <div className="tt-page" style={{ maxWidth: 1100 }}>
      {/* Shared header — Monthly health / By person toggle (left) + the
          month picker (right). The month drives both reads. */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            padding: 4,
            background: '#fff',
            border: '1px solid var(--sp-border)',
            borderRadius: 10,
            width: 'fit-content',
          }}
        >
          <SwitchTab
            active={mode === 'overview'}
            onClick={() => setMode('overview')}
          >
            <Icon name="chart" size={13} /> Monthly health
          </SwitchTab>
          <SwitchTab active={mode === 'detail'} onClick={() => setMode('detail')}>
            <Icon name="users" size={13} /> By person
            <span
              style={{
                marginLeft: 4,
                padding: '0 6px',
                borderRadius: 999,
                background: 'var(--sp-bg-muted)',
                fontSize: 11,
                color: 'var(--sp-fg-2)',
              }}
            >
              {rows.length}
            </span>
          </SwitchTab>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ width: 200 }}>
          <Select
            value={month}
            options={monthOptions}
            getKey={(o) => o.key}
            getLabel={(o) => o.label}
            onChange={(v) => setMonth(v)}
          />
        </div>
      </div>

      {mode === 'overview' ? (
        loading ? (
          <div
            className="card"
            style={{
              padding: '40px 16px',
              borderRadius: 14,
              textAlign: 'center',
              color: 'var(--sp-fg-3)',
              fontSize: 14,
            }}
          >
            <span
              className="tt-spinner sm"
              style={{ marginRight: 8, verticalAlign: 'middle' }}
            />
            Loading team data…
          </div>
        ) : error ? (
          <div
            className="card"
            style={{
              padding: '40px 16px',
              borderRadius: 14,
              textAlign: 'center',
              color: 'var(--sp-fg-3)',
              fontSize: 14,
            }}
          >
            {error}
          </div>
        ) : (
          <TeamHealth
            rows={tracked}
            exemptCount={exempt.length}
            monthLabel={monthLabel}
          />
        )
      ) : (
        <>
          {/* Filter pills + bulk approve — per-person mode only. */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              marginBottom: 16,
              flexWrap: 'wrap',
            }}
          >
            <div style={{ display: 'flex', gap: 6 }}>
              {TEAM_FILTERS.map((opt) => (
                <button
                  key={opt.k}
                  onClick={() => setFilter(opt.k)}
                  style={{
                    all: 'unset',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '6px 12px',
                    borderRadius: 999,
                    fontSize: 13,
                    fontWeight: 600,
                    color: filter === opt.k ? '#fff' : 'var(--sp-fg-2)',
                    background:
                      filter === opt.k
                        ? 'var(--sp-fg-1)'
                        : 'var(--sp-bg-muted)',
                    border:
                      '1px solid ' +
                      (filter === opt.k ? 'var(--sp-fg-1)' : 'var(--sp-border)'),
                  }}
                >
                  {opt.label}
                  <span
                    style={{
                      padding: '0 6px',
                      borderRadius: 999,
                      background:
                        filter === opt.k ? 'rgba(255,255,255,0.18)' : '#fff',
                      fontSize: 11,
                      fontWeight: 700,
                      color: filter === opt.k ? '#fff' : 'var(--sp-fg-3)',
                    }}
                  >
                    {counts[opt.k]}
                  </span>
                </button>
              ))}
            </div>
            <div style={{ flex: 1 }} />
            <button
              onClick={approveSelected}
              disabled={checkedEmails.length === 0 || !!busyEmail}
              className="btn btn-primary"
              style={{
                whiteSpace: 'nowrap',
                ...(checkedEmails.length === 0
                  ? { opacity: 0.4, cursor: 'not-allowed' }
                  : {}),
              }}
            >
              <Icon name="check" size={13} stroke={2.4} />
              &nbsp;Approve {checkedEmails.length || ''} selected
            </button>
          </div>

          <div
            className="card"
            style={{ padding: 0, borderRadius: 14, overflow: 'hidden' }}
          >
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: TEAM_GRID_COLUMNS,
            alignItems: 'center',
            padding: '12px 16px',
            borderBottom: '1px solid var(--sp-border)',
            background: 'var(--sp-bg-page)',
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: 'var(--sp-fg-3)',
          }}
        >
          <div>
            <input
              type="checkbox"
              checked={allChecked}
              ref={(el) => {
                if (el) el.indeterminate = someChecked;
              }}
              onChange={toggleAll}
              disabled={selectableEmails.length === 0}
              title={allChecked ? 'Deselect all pending' : 'Select all pending'}
              style={{
                width: 15,
                height: 15,
                accentColor: 'var(--sp-fg-1)',
                cursor:
                  selectableEmails.length === 0 ? 'not-allowed' : 'pointer',
              }}
            />
          </div>
          <div>Report</div>
          <div>Reported</div>
          <div>Missing</div>
          <div>Overtime</div>
          <div>Status</div>
          <div style={{ textAlign: 'right' }}>Approval</div>
        </div>
        {loading ? (
          <div
            style={{
              padding: '40px 16px',
              textAlign: 'center',
              color: 'var(--sp-fg-3)',
              fontSize: 14,
            }}
          >
            <span
              className="tt-spinner sm"
              style={{ marginRight: 8, verticalAlign: 'middle' }}
            />
            Loading team data…
          </div>
        ) : error ? (
          <div
            style={{
              padding: '40px 16px',
              textAlign: 'center',
              color: 'var(--sp-fg-3)',
              fontSize: 14,
            }}
          >
            {error}
          </div>
        ) : filtered.length === 0 && !showExcludedGroup ? (
          <div
            style={{
              padding: '40px 16px',
              textAlign: 'center',
              color: 'var(--sp-fg-3)',
              fontSize: 14,
            }}
          >
            No reports match this filter.
          </div>
        ) : (
          <>
            {filtered.map((row, i) => (
              <TeamRow
                key={row.email}
                row={row}
                isLast={i === filtered.length - 1 && !showExcludedGroup}
                role={role}
                busy={busyEmail === row.email || busyEmail === '__all'}
                checked={selected.has(row.email)}
                onToggle={() => toggleOne(row.email)}
                onDrill={() => onDrillIn?.(row.email)}
                onApprove={() => approveSingle(row)}
                onReopen={() => reopen(row)}
              />
            ))}
            {/* v2.6 — Excluded users: still listed so approvers know they
                exist, but with no metrics / status / approval and clearly
                marked. Shown under the All filter only. */}
            {showExcludedGroup && (
              <>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '10px 16px',
                    background: 'var(--sp-bg-page)',
                    borderTop: '1px solid var(--sp-border)',
                    borderBottom: '1px solid var(--sp-border-soft)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      color: 'var(--sp-fg-3)',
                    }}
                  >
                    Excluded · not required to log
                  </span>
                  <span style={{ fontSize: 11, color: 'var(--sp-fg-4)' }}>
                    excluded from stats &amp; notifications
                  </span>
                </div>
                {exempt.map((row, i) => (
                  <TeamRow
                    key={row.email}
                    row={row}
                    isLast={i === exempt.length - 1}
                    role={role}
                    onDrill={() => onDrillIn?.(row.email)}
                  />
                ))}
              </>
            )}
          </>
        )}
          </div>
        </>
      )}

      <ConfirmDialog
        open={!!confirm}
        {...(confirm || {})}
        busy={!!busyEmail}
        onCancel={() => {
          if (!busyEmail) setConfirm(null);
        }}
      />
    </div>
  );
}

// Segmented-control tab — bordered white container, dark active pill. Shared
// look between the Monthly health / By person toggle here and the top-bar
// subject switcher. Labels stay single-line so the control never wraps.
function SwitchTab({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      style={{
        all: 'unset',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '6px 11px',
        borderRadius: 8,
        whiteSpace: 'nowrap',
        fontSize: 13,
        fontWeight: 600,
        color: active ? '#fff' : 'var(--sp-fg-2)',
        background: active ? 'var(--sp-fg-1)' : 'transparent',
        transition: 'background .12s, color .12s',
      }}
    >
      {children}
    </button>
  );
}

function TeamRow({
  row,
  role,
  busy,
  isLast,
  checked,
  onToggle,
  onDrill,
  onApprove,
  onReopen,
}) {
  // v2.6 — excluded user: no checkbox, no metrics, no status, no approval.
  // Just identity + a muted "Not required to log time" + the Excluded
  // badge. Still clickable to drill in (read-only, optional-logging view).
  if (row.loggingExempt) {
    return (
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '32px 2.4fr 3.6fr 1.3fr',
          alignItems: 'center',
          padding: '14px 16px',
          borderBottom: isLast ? 0 : '1px solid var(--sp-border-soft)',
          cursor: 'pointer',
          transition: 'background .12s',
        }}
        onClick={onDrill}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = 'var(--sp-bg-page)')
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        <div />
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            minWidth: 0,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: 'var(--sp-bg-muted)',
              color: 'var(--sp-fg-3)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 11,
              fontWeight: 700,
              flex: 'none',
            }}
          >
            {(row.name || '?')
              .split(' ')
              .map((s) => s[0])
              .slice(0, 2)
              .join('')}
          </span>
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 14,
                fontWeight: 600,
                color: 'var(--sp-fg-1)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              {row.name}
              <span aria-hidden="true" style={{ fontSize: 11 }}>
                {FLAG_EMOJI[row.team] || ''}
              </span>
            </div>
            <div
              style={{ fontSize: 11, color: 'var(--sp-fg-3)', lineHeight: 1.5 }}
            >
              <div>{row.email}</div>
              {row.department && (
                <div style={{ fontWeight: 600, color: 'var(--sp-fg-2)' }}>
                  {row.department}
                </div>
              )}
            </div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: 'var(--sp-fg-3)' }}>
          Not required to log time
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <ExcludedBadge />
        </div>
      </div>
    );
  }

  const approved = row.approval.state === 'approved';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: TEAM_GRID_COLUMNS,
        alignItems: 'center',
        padding: '14px 16px',
        borderBottom: isLast ? 0 : '1px solid var(--sp-border-soft)',
        cursor: 'pointer',
        transition: 'background .12s',
      }}
      onClick={onDrill}
      onMouseEnter={(e) =>
        (e.currentTarget.style.background = 'var(--sp-bg-page)')
      }
      onMouseLeave={(e) =>
        (e.currentTarget.style.background = 'transparent')
      }
    >
      <div onClick={(e) => e.stopPropagation()}>
        {approved ? (
          <span
            style={{
              width: 15,
              height: 15,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'var(--sp-fg-4)',
            }}
          >
            <Icon name="lock" size={12} />
          </span>
        ) : (
          <input
            type="checkbox"
            checked={!!checked}
            onChange={onToggle}
            style={{
              width: 15,
              height: 15,
              accentColor: 'var(--sp-fg-1)',
              cursor: 'pointer',
            }}
          />
        )}
      </div>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}
      >
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: 'var(--sp-mint-100)',
            color: '#007C7C',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 11,
            fontWeight: 700,
            flex: 'none',
          }}
        >
          {(row.name || '?')
            .split(' ')
            .map((s) => s[0])
            .slice(0, 2)
            .join('')}
        </span>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--sp-fg-1)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            {row.name}
            <span aria-hidden="true" style={{ fontSize: 11 }}>
              {FLAG_EMOJI[row.team] || ''}
            </span>
          </div>
          {/* v2.0 follow-up — surface department under the email so
              approvers can scan org structure without drilling in.
              Stacked vertically (one metadata line per row) reads more
              cleanly than the inline `·` separator we tried first. */}
          <div
            style={{
              fontSize: 11,
              color: 'var(--sp-fg-3)',
              lineHeight: 1.5,
            }}
          >
            <div>{row.email}</div>
            {row.department && (
              <div style={{ fontWeight: 600, color: 'var(--sp-fg-2)' }}>
                {row.department}
              </div>
            )}
          </div>
        </div>
      </div>
      <div
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: 'var(--sp-fg-1)',
        }}
      >
        {fmtHM(row.reportedMins)}
        <span
          style={{
            color: 'var(--sp-fg-3)',
            fontWeight: 500,
            marginLeft: 4,
            fontSize: 12,
          }}
        >
          / {fmtHM(row.expectedMins)}
        </span>
      </div>
      <div
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: row.missingMins > 0 ? '#B57651' : 'var(--sp-fg-3)',
        }}
      >
        {row.missingMins > 0 ? fmtHM(row.missingMins) : '—'}
      </div>
      <div
        style={{
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: row.overtimeMins > 0 ? '#B57651' : 'var(--sp-fg-3)',
        }}
      >
        {row.overtimeMins > 0 ? `+${fmtHM(row.overtimeMins)}` : '—'}
      </div>
      <div>
        <StatusPill kind={row.status} size="sm" inGrace={row.inGrace} />
      </div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          gap: 6,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {approved ? (
          <>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
                color: 'var(--sp-fg-3)',
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              <Icon name="lock" size={11} /> Approved
            </span>
            {role === 'super_approver' && (
              <button
                onClick={onReopen}
                className="btn btn-ghost btn-sm"
                disabled={busy}
                title="Reopen this month"
              >
                Reopen
              </button>
            )}
          </>
        ) : (
          <button
            onClick={onApprove}
            className="btn btn-secondary btn-sm"
            disabled={busy}
            style={{ whiteSpace: 'nowrap' }}
          >
            {busy ? (
              <span className="tt-spinner sm" style={{ marginRight: 6 }} />
            ) : (
              <Icon name="check" size={12} stroke={2.4} />
            )}
            &nbsp;Approve
          </button>
        )}
      </div>
    </div>
  );
}
